/**
 * Tick generation utilities for chart axes.
 * Pure functions for generating axis ticks - no React dependencies.
 */

import type { TimeFormatPrecision } from "./formatters";
import { isTimestampInGap } from "./intervalUtils";
import type { DataGap, Scale } from "~types/types";

// ==================== Types ====================

export interface GenerateTicksResult {
  ticks: number[];
}

export interface TimeAxisTick {
  timestamp: number;
  x: number;
  label: string;
  isVisible: boolean;
}

export interface ValueAxisTick {
  value: number;
  y: number;
  label: string;
}

export interface SecondaryLabel {
  x: number;
  label: string;
  timestamp: number;
}

// ==================== Constants ====================

const INTRADAY_INTERVALS = [
  5, 10, 15, 30, 45, 60, 75, 90, 120, 135, 150, 165, 180,
];

// ==================== Secondary Label Utilities ====================

/**
 * Determine secondary label type based on precision.
 * Secondary label provides context one level higher than primary:
 * - hour/minute precision -> show date
 * - day/month precision -> show year
 * - year precision -> no secondary label
 */
export function getSecondaryLabelType(
  precision: TimeFormatPrecision
): "date" | "year" | null {
  switch (precision) {
    case "minute":
    case "hour":
      return "date";
    case "day":
    case "month":
      return "year";
    case "year":
      return null;
    default:
      return null;
  }
}

/**
 * Format secondary label based on type.
 */
export function formatSecondaryLabel(
  timestamp: number,
  labelType: "date" | "year",
  locale: string
): string {
  const date = new Date(timestamp);

  try {
    if (labelType === "date") {
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
      }).format(date);
    }

    if (labelType === "year") {
      return new Intl.DateTimeFormat(locale, { year: "numeric" }).format(date);
    }
  } catch {
    // Fallback for unsupported locales
  }

  if (labelType === "year") return date.getFullYear().toString();

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * Check if two timestamps are in different periods (for secondary label changes).
 */
export function isInDifferentPeriod(
  ts1: number,
  ts2: number,
  labelType: "date" | "year"
): boolean {
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);

  if (labelType === "year") {
    return d1.getFullYear() !== d2.getFullYear();
  }

  // For date, check if day changed
  return (
    d1.getFullYear() !== d2.getFullYear() ||
    d1.getMonth() !== d2.getMonth() ||
    d1.getDate() !== d2.getDate()
  );
}

// ==================== Interval Utilities ====================

/**
 * Determine nice tick interval (in minutes) based on visible range and target tick count.
 * Finds the interval that produces tick count closest to target.
 */
export function getNiceInterval(
  rangeMs: number,
  targetCount: number,
  intervals?: number[]
): number {
  const niceIntervals: number[] = intervals ?? [];

  // Generate nice intervals from 1h to 24h in 30-minute increments
  if (!niceIntervals.length) {
    for (let minutes = 60; minutes <= 14400; minutes += 30) {
      niceIntervals.push(minutes);
    }
  }

  let bestInterval = niceIntervals[0];
  let smallestDifference = Infinity;

  niceIntervals.forEach((interval) => {
    // Calculate the range this interval would cover with targetCount ticks
    const intervalMs = interval * 60 * 1000;
    const calculatedRangeMs = intervalMs * targetCount;

    // Find absolute difference from actual range
    const difference = Math.abs(calculatedRangeMs - rangeMs);

    // Keep interval with smallest difference
    if (difference < smallestDifference) {
      smallestDifference = difference;
      bestInterval = interval;
    }
  });

  return bestInterval;
}

/**
 * Round timestamp down to the nearest interval boundary.
 */
export function floorToInterval(
  timestamp: number,
  intervalMinutes: number
): number {
  const date = new Date(timestamp);
  const minutes = date.getMinutes();
  const flooredMinutes =
    Math.floor(minutes / intervalMinutes) * intervalMinutes;

  date.setMinutes(flooredMinutes, 0, 0);

  return date.getTime();
}

// ==================== Time Axis Tick Generation ====================

/**
 * Generate evenly-spaced ticks at nice intervals.
 * For intraday (<= 30m): uses 5m, 10m, 15m, 30m, etc. intervals.
 * For multi-day (<= 1h): uses 1h, 1.5h, 2h, ..., 24h intervals.
 */
export function generateNiceIntervalTicks(
  xScale: Pick<Scale, "domain" | "invert">,
  _pixelRange: [number, number],
  targetCount: number,
  dataIntervalMs: number
): GenerateTicksResult {
  const domain = xScale.domain();
  const [startTime, endTime] = domain;
  const rangeMs = endTime - startTime;

  if (rangeMs <= 0 || targetCount <= 0) {
    return { ticks: [] };
  }

  // Determine nice interval based on data interval type
  const intervalMinutes =
    dataIntervalMs <= 30 * 60 * 1000
      ? getNiceInterval(rangeMs, targetCount, INTRADAY_INTERVALS)
      : getNiceInterval(rangeMs, targetCount);

  const intervalMs = intervalMinutes * 60 * 1000;

  // Find first tick position (aligned to interval boundary after start time)
  const firstTick = floorToInterval(startTime, intervalMinutes);
  const startTick = firstTick >= startTime ? firstTick : firstTick + intervalMs;

  // Generate ticks at regular intervals
  const ticks: number[] = [];
  let currentTick = startTick;

  while (currentTick <= endTime && ticks.length < targetCount * 2) {
    if (currentTick >= startTime) {
      ticks.push(currentTick);
    }
    currentTick += intervalMs;
  }

  return { ticks };
}

/**
 * Generate ticks by dividing x-axis into equal pixel segments.
 */
export function generateTimeAxisTicks(
  xScale: Pick<Scale, "domain" | "invert">,
  pixelRange: [number, number],
  targetCount: number
): GenerateTicksResult {
  const [pixelStart, pixelEnd] = pixelRange;
  const pixelWidth = pixelEnd - pixelStart;

  if (pixelWidth <= 0 || targetCount <= 0) {
    return { ticks: [] };
  }

  // Calculate pixel step for equal distribution
  const pixelStep = pixelWidth / (targetCount + 1);

  const ticks: number[] = [];

  for (let i = 1; i <= targetCount; i++) {
    const pixelPos = pixelStart + i * pixelStep;
    const timestamp = xScale.invert(pixelPos);

    ticks.push(timestamp);
  }

  return { ticks };
}

/**
 * Generate equidistant ticks in pixel space, filtering out those in gaps.
 * This ensures ticks are visually evenly spaced regardless of time gaps.
 *
 * @param xScale - The x-axis scale (can be compressed or linear)
 * @param pixelRange - [start, end] pixel range for tick placement
 * @param targetCount - Desired number of ticks
 * @param gaps - Optional array of data gaps to filter out
 * @returns Array of timestamps for tick positions
 */
export function generateEquidistantTicks(
  xScale: Pick<Scale, "invert">,
  pixelRange: [number, number],
  targetCount: number,
  gaps?: DataGap[]
): GenerateTicksResult {
  const [pixelStart, pixelEnd] = pixelRange;
  const pixelWidth = pixelEnd - pixelStart;

  if (pixelWidth <= 0 || targetCount <= 0) {
    return { ticks: [] };
  }

  // Calculate pixel step for equal visual distribution
  const pixelStep = pixelWidth / (targetCount + 1);

  const ticks: number[] = [];

  for (let i = 1; i <= targetCount; i++) {
    const pixelPos = pixelStart + i * pixelStep;
    const timestamp = xScale.invert(pixelPos);

    // Skip ticks that fall in large gaps
    if (!gaps || !isTimestampInGap(timestamp, gaps, true)) {
      ticks.push(timestamp);
    }
  }

  return { ticks };
}

/**
 * Generate ticks optimized for compressed time scale with gaps.
 * Combines equidistant placement with nice interval rounding.
 *
 * @param xScale - The x-axis scale
 * @param pixelRange - [start, end] pixel range
 * @param targetCount - Desired number of ticks
 * @param dataIntervalMs - The data interval in milliseconds (e.g., 5 min = 300000)
 * @param gaps - Optional array of data gaps
 * @returns Ticks that are visually equidistant and avoid gaps
 */
export function generateCompressedScaleTicks(
  xScale: Pick<Scale, "domain" | "invert">,
  pixelRange: [number, number],
  targetCount: number,
  dataIntervalMs: number,
  gaps?: DataGap[]
): GenerateTicksResult {
  // Generate equidistant ticks first
  const equidistantResult = generateEquidistantTicks(
    xScale,
    pixelRange,
    targetCount,
    gaps
  );

  // If no data interval specified, return equidistant ticks as-is
  if (!dataIntervalMs || dataIntervalMs <= 0) {
    return equidistantResult;
  }

  // Round each tick to the nearest interval boundary for nicer labels
  const roundedTicks = equidistantResult.ticks.map((timestamp) => {
    const remainder = timestamp % dataIntervalMs;

    // Round to nearest interval
    if (remainder < dataIntervalMs / 2) {
      return timestamp - remainder;
    }

    return timestamp + (dataIntervalMs - remainder);
  });

  // Remove duplicates that may occur after rounding
  const uniqueTicks = [...new Set(roundedTicks)].sort((a, b) => a - b);

  // Filter out any that now fall in gaps after rounding
  const filteredTicks = gaps
    ? uniqueTicks.filter((ts) => !isTimestampInGap(ts, gaps, true))
    : uniqueTicks;

  return { ticks: filteredTicks };
}

const MAX_TICK_COUNT = 100;

/**
 * Round a value to a "nice" number (1, 2, 2.5, or 5 × 10^n).
 * When round=true, uses tighter thresholds for step-size rounding.
 * When round=false, uses ceiling-style thresholds for range expansion.
 */
function niceNum(value: number, round: boolean): number {
  if (value === 0) return 1;

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const fraction = Math.abs(value) / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 2.25) niceFraction = 2;
    else if (fraction < 3.5) niceFraction = 2.5;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 2.5) niceFraction = 2.5;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * Math.pow(10, exponent);
}

/**
 * Generate human-readable "nice" tick values for a numeric axis.
 * Expands the domain to align with nice step boundaries (1, 2, 2.5, 5 multiples).
 *
 * @param domain - [min, max] data range
 * @param maxTicks - desired maximum number of ticks
 * @returns array of nice tick values covering the domain
 */
export function generateNiceYTicks(
  domain: [number, number],
  maxTicks: number
): number[] {
  const [min, max] = domain;
  const range = max - min;

  if (range === 0) return [min];
  if (maxTicks <= 1) return [min];

  const niceStep = niceNum(range / (maxTicks - 1), true);
  const startIndex = Math.floor(min / niceStep);
  const endIndex = Math.ceil(max / niceStep);

  const ticks: number[] = [];

  for (
    let i = startIndex;
    i <= endIndex && ticks.length < MAX_TICK_COUNT;
    i++
  ) {
    ticks.push(i * niceStep);
  }

  return ticks;
}

// ==================== Secondary Labels Computation ====================

/**
 * Compute secondary labels (date/year) centered in their respective period segments.
 */
export function computeSecondaryLabels(
  ticks: Array<{ timestamp: number; x: number }>,
  secondaryLabelType: "date" | "year" | null,
  chartLeft: number,
  chartRight: number,
  locale: string
): SecondaryLabel[] {
  if (!secondaryLabelType || ticks.length === 0) {
    return [];
  }

  // Find period boundary positions (where date/month/year changes)
  const boundaryPositions: number[] = [];

  for (let i = 1; i < ticks.length; i++) {
    const prevTick = ticks[i - 1];
    const tick = ticks[i];

    if (
      prevTick &&
      tick &&
      isInDifferentPeriod(
        prevTick.timestamp,
        tick.timestamp,
        secondaryLabelType
      )
    ) {
      // Boundary is at the midpoint between the two ticks
      boundaryPositions.push((prevTick.x + tick.x) / 2);
    }
  }

  // Create segments: [chartLeft, boundary1], [boundary1, boundary2], ..., [boundaryN, chartRight]
  const segmentEdges = [chartLeft, ...boundaryPositions, chartRight];
  const labels: SecondaryLabel[] = [];

  for (let i = 0; i < segmentEdges.length - 1; i++) {
    const leftEdge = segmentEdges[i];
    const rightEdge = segmentEdges[i + 1];

    if (leftEdge === undefined || rightEdge === undefined) continue;

    // Center of this segment
    const centerX = (leftEdge + rightEdge) / 2;

    // Find a tick in this segment to get the timestamp for the label
    const tickInSegment = ticks.find(
      (t) => t.x >= leftEdge && t.x <= rightEdge
    );

    if (tickInSegment) {
      labels.push({
        x: centerX,
        label: formatSecondaryLabel(
          tickInSegment.timestamp,
          secondaryLabelType,
          locale
        ),
        timestamp: tickInSegment.timestamp,
      });
    }
  }

  return labels;
}

export interface IndexedTick {
  index: number;
  timestamp: number;
}

/**
 * Convert step count to milliseconds. Rounds to nearest intervalMs multiple,
 * minimum 1 interval.
 */
function getStepMs(
  intervalMs: number,
  targetCount: number,
  totalDuration: number
): number {
  const rawStep = totalDuration / targetCount;

  return Math.max(intervalMs, Math.round(rawStep / intervalMs) * intervalMs);
}

/**
 * Binary search: returns leftmost index where arr[index] >= value.
 */
function bisectLeft(arr: number[], value: number): number {
  let lo = 0;
  let hi = arr.length;

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);

    if (arr[mid] !== undefined && arr[mid] < value) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  return lo;
}

/**
 * Snap a target timestamp to the nearest actual timestamp in the series.
 */
function snapToNearestPoint(timestamps: number[], target: number): number {
  if (timestamps.length === 0) return target;

  const idx = bisectLeft(timestamps, target);

  if (idx === 0) return timestamps[0] ?? target;
  if (idx >= timestamps.length) return timestamps.at(-1) ?? target;

  const before = timestamps[idx - 1] ?? target;
  const after = timestamps[idx] ?? target;

  return Math.abs(target - before) <= Math.abs(after - target) ? before : after;
}

/**
 * Remove duplicate values from array while preserving order.
 */
function deduplicateTicks(ticks: number[]): number[] {
  return [...new Set(ticks)];
}

/**
 * Group consecutive data points into sessions (contiguous sequences without large gaps).
 * A "large gap" is any gap exceeding 3x the interval.
 * Returns array of arrays, each containing the timestamps of one session.
 */
function buildSessions(
  series: { timestamp: number }[],
  intervalMs: number
): number[][] {
  if (series.length === 0) return [];

  const gapThreshold = intervalMs * 3;
  const sessions: number[][] = [];
  const firstItem = series[0];

  if (!firstItem) return [];

  let currentSession: number[] = [firstItem.timestamp];

  for (let i = 1; i < series.length; i++) {
    const prev = series[i - 1];
    const curr = series[i];

    if (!prev || !curr) continue;
    const gap = curr.timestamp - prev.timestamp;

    if (gap > gapThreshold) {
      sessions.push(currentSession);
      currentSession = [curr.timestamp];
    } else {
      currentSession.push(curr.timestamp);
    }
  }

  sessions.push(currentSession);

  return sessions;
}

/**
 * Map a cumulative time offset to a real timestamp using session boundaries.
 * Cumulative time collapses gaps: session durations are preserved, inter-session
 * gaps are not counted.
 */
function cumulativeToTimestamp(
  sessions: number[][],
  cumulativeMs: number
): number {
  let accumulated = 0;

  for (const session of sessions) {
    if (session.length === 0) continue;

    const sessionStart = session[0];
    const sessionEnd = session.at(-1);

    if (sessionStart === undefined || sessionEnd === undefined) continue;
    const sessionDuration = sessionEnd - sessionStart;

    if (cumulativeMs <= accumulated + sessionDuration) {
      return sessionStart + (cumulativeMs - accumulated);
    }

    accumulated += sessionDuration;
  }

  const lastSession = sessions.at(-1);

  return lastSession?.at(-1) ?? 0;
}

/**
 * Standard x-axis ticks for non-intraday data.
 * Steps through the series time range at regular intervals and snaps to
 * nearest actual data point.
 */
export function getStandardXAxisTicks(
  series: { timestamp: number }[],
  targetCount: number,
  intervalMs: number
): GenerateTicksResult {
  if (series.length === 0) return { ticks: [] };

  const timestamps = series.map((p) => p.timestamp);
  const first = timestamps[0];
  const last = timestamps.at(-1);

  if (first === undefined || last === undefined) return { ticks: [] };

  const totalDuration = last - first;

  if (totalDuration === 0) return { ticks: [first] };

  const stepMs = getStepMs(intervalMs, targetCount, totalDuration);
  const ticks: number[] = [];

  for (let t = first + stepMs; t < last; t += stepMs) {
    ticks.push(snapToNearestPoint(timestamps, t));
  }

  return { ticks: deduplicateTicks(ticks) };
}

/**
 * Intraday x-axis ticks using cumulative timeline algorithm.
 * Collapses overnight and weekend gaps so ticks are distributed evenly
 * across actual trading time, not wall-clock time.
 *
 * Use for 1D charts where simple timestamp stepping would cluster all ticks
 * at session boundaries because of overnight gaps.
 */
export function getIntradayXAxisTicks(
  series: { timestamp: number }[],
  targetCount: number,
  intervalMs: number
): GenerateTicksResult {
  if (series.length === 0) return { ticks: [] };

  const sessions = buildSessions(series, intervalMs);
  const timestamps = series.map((p) => p.timestamp);

  const totalCumulativeMs = sessions.reduce((sum, session) => {
    if (session.length < 2) return sum;

    const sessionEnd = session.at(-1);
    const sessionStart = session[0];

    if (sessionEnd === undefined || sessionStart === undefined) return sum;

    return sum + (sessionEnd - sessionStart);
  }, 0);

  const firstTimestamp = series[0]?.timestamp;

  if (totalCumulativeMs === 0) {
    return { ticks: firstTimestamp ? [firstTimestamp] : [] };
  }

  const stepMs = getStepMs(intervalMs, targetCount, totalCumulativeMs);
  const ticks: number[] = [];

  for (
    let cumulative = stepMs;
    cumulative < totalCumulativeMs;
    cumulative += stepMs
  ) {
    const realTimestamp = cumulativeToTimestamp(sessions, cumulative);

    ticks.push(snapToNearestPoint(timestamps, realTimestamp));
  }

  return { ticks: deduplicateTicks(ticks) };
}
